import { useFormik } from "formik";
import Select from "../../../components/select";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useListDews } from "../layout/resuableEffect";
import {
  confirmResizeServer,
  resizeServer,
  stopResizeServer,
  listServer,
} from "../../../hooks/local/userReducer";
import { toast } from "react-toastify";
import closeIcon from "../../../assets/svg-icons/closeBrand.svg";
import { useState, useEffect, useRef } from "react";
import Input from "../../../components/input";
import Button from "../../../components/button";
import { Backdrop, CircularProgress } from "@mui/material";
import PaymentModal from "../../../components/paymentModal";

const ResizeInstance = () => {
  document.title = "Resize Instance - Cloudvantage";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currency = useSelector((state) => state.user.currency);
  const selectedProject = useSelector((state) => state.user.selectedProject);
  const selectedServer = JSON.parse(sessionStorage.getItem("selectedServer"));
  const walletBalance = useSelector((state) =>
    state.user.virtualAccount
      ? currency === "USD"
        ? state.user.virtualAccount.dollar_balance
        : state.user.virtualAccount.balance
      : 0
  );
  const virtualAccount = useSelector((state) => state.user.virtualAccount);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingInstance, setIsLoadingInstance] = useState(false);
  const [customizeModal, setCustomizeModal] = useState(false);
  const [resizeType, setResizeType] = useState("preconfigured");
  const [continueResize, setContinueResize] = useState(null);
  const [serverDetails, setServerDetails] = useState(null);
  const [isFetchingServer, setIsFetchingServer] = useState(true);
  const [customFlavorId, setCustomFlavorId] = useState(null);
  const [lastCustomizedFlavor, setLastCustomizedFlavor] = useState(null);
  const [lastPreconfiguredFlavor, setLastPreconfiguredFlavor] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [calculatedEstimate, setCalculatedEstimate] = useState("0.00");

  const returnedDewsList = useListDews();
  const dewsList = returnedDewsList?.results;

  const dewsListMap = Array.isArray(dewsList)
    ? dewsList.map((dew) => ({
        id: dew?.id,
        flavorId: dew?.flavor_id,
        name: dew?.name,
        ram: parseInt(dew?.ram?.replace("GB", "")),
        disk: parseInt(dew?.disk?.replace("GB", "")),
        vCPUs: dew?.vcpus,
        price: dew?.naira_price,
        dollarPrice: dew?.dollar_price,
      }))
    : [];

  const isCustomServer =
    selectedServer?.flavorId?.endsWith(".custom.flavor") ||
    selectedServer?.flavorId?.includes(".upgrade");

  const hasFetchedServer = useRef(false);

  useEffect(() => {
    const fetchServerDetails = async () => {
      if (hasFetchedServer.current) return;
      hasFetchedServer.current = true;

      setIsFetchingServer(true);
      try {
        const serverId = selectedServer?.serverId;
        const projectId = selectedProject.id;
        const { payload } = await dispatch(listServer({ projectId, serverId }));
        if (payload?.statuscode === "00" || payload?.flavor) {
          setServerDetails(payload);
        } else {
          toast.error("Failed to fetch server details");
        }
      } catch (error) {
        toast.error("Error fetching server details: " + error.message);
      } finally {
        setIsFetchingServer(false);
      }
    };

    if (selectedServer?.serverId && selectedProject?.id && !serverDetails) {
      fetchServerDetails();
    } else {
      setIsFetchingServer(false);
    }
  }, [dispatch, selectedServer?.serverId, selectedProject?.id, serverDetails]);

  useEffect(() => {
    if (isCustomServer) {
      setResizeType("customized");
      setCustomizeModal(true);
    } else {
      setResizeType("preconfigured");
    }
  }, [isCustomServer]);

  const currentFlavor = serverDetails?.flavor
    ? {
        id: serverDetails.flavor.id,
        name: serverDetails.name || "Current Flavor",
        flavorId: serverDetails?.flavor?.id,
        vCPUs: serverDetails.flavor.vcpus || 1,
        ram: serverDetails.flavor.ram
          ? parseInt(serverDetails?.flavor?.ram?.replace("GB", ""))
          : 1,
        disk: serverDetails.flavor.disk || 5,
        price:
          dewsListMap.find((f) => f.id === serverDetails.flavor.id)?.price || 0,
        dollarPrice:
          dewsListMap.find((f) => f.id === serverDetails.flavor.id)?.dollarPrice ||
          0,
      }
    : dewsListMap.find((flavor) => flavor.id === selectedServer?.flavorId) || {
        vCPUs: 1,
        ram: 1,
        disk: 5,
        price: 0,
        name: "Current Flavor",
      };

  const flavorOptions = dewsListMap
    .filter((flavor) => {
      const isCustomFlavor =
        currentFlavor.id?.endsWith(".custom.flavor") ||
        currentFlavor.id?.includes(".upgrade");

      if (isCustomFlavor) {
        return flavor.id !== currentFlavor.id && flavor.disk > currentFlavor.disk;
      } else {
        return (
          flavor.id !== currentFlavor.id &&
          ((flavor.vCPUs >= currentFlavor.vCPUs &&
            flavor.ram >= currentFlavor.ram &&
            flavor.disk >= currentFlavor.disk) ||
            flavor.price > currentFlavor.price)
        );
      }
    })
    .map((flavor) => ({
      value: flavor.id,
      label: flavor.name,
      flavorId: flavor.flavorId,
    }));

  const preconfiguredOptions = [
    { value: "", label: "Select a New Flavor" },
    ...flavorOptions,
  ];

  const baseFlavor =
    resizeType === "customized" && lastPreconfiguredFlavor
      ? lastPreconfiguredFlavor
      : currentFlavor;

  const VCPUOptions = [
    { value: 1, label: "1" },
    { value: 2, label: "2" },
    { value: 4, label: "4" },
    { value: 6, label: "6" },
    { value: 8, label: "8" },
    { value: 12, label: "12" },
    { value: 16, label: "16" },
    { value: 24, label: "24" },
    { value: 32, label: "32" },
    { value: 48, label: "48" },
    { value: 64, label: "64" },
    { value: 128, label: "128" },
    { value: 256, label: "256" },
    { value: 512, label: "512" },
  ];

  const RAMOptions = [
    { value: "1GB", label: "1GB", unit: 1 },
    { value: "2GB", label: "2GB", unit: 2 },
    { value: "4GB", label: "4GB", unit: 4 },
    { value: "6GB", label: "6GB", unit: 6 },
    { value: "8GB", label: "8GB", unit: 8 },
    { value: "12GB", label: "12GB", unit: 12 },
    { value: "16GB", label: "16GB", unit: 16 },
    { value: "24GB", label: "24GB", unit: 24 },
    { value: "32GB", label: "32GB", unit: 32 },
    { value: "48GB", label: "48GB", unit: 48 },
    { value: "64GB", label: "64GB", unit: 64 },
    { value: "128GB", label: "128GB", unit: 128 },
    { value: "256GB", label: "256GB", unit: 256 },
    { value: "512GB", label: "512GB", unit: 512 },
  ];

  const StorageOptions = [
    { value: "5GB", label: "5GB", unit: 5 },
    { value: "6GB", label: "6GB", unit: 6 },
    { value: "7GB", label: "7GB", unit: 7 },
    { value: "8GB", label: "8GB", unit: 8 },
    { value: "9GB", label: "9GB", unit: 9 },
    { value: "10GB", label: "10GB", unit: 10 },
    { value: "11GB", label: "11GB", unit: 11 },
    { value: "12GB", label: "12GB", unit: 12 },
    { value: "13GB", label: "13GB", unit: 13 },
    { value: "14GB", label: "14GB", unit: 14 },
    { value: "15GB", label: "15GB", unit: 15 },
    { value: "20GB", label: "20GB", unit: 20 },
    { value: "25GB", label: "25GB", unit: 25 },
    { value: "30GB", label: "30GB", unit: 30 },
    { value: "35GB", label: "35GB", unit: 35 },
    { value: "40GB", label: "40GB", unit: 40 },
    { value: "45GB", label: "45GB", unit: 45 },
    { value: "50GB", label: "50GB", unit: 50 },
    { value: "55GB", label: "55GB", unit: 55 },
    { value: "60GB", label: "60GB", unit: 60 },
    { value: "65GB", label: "65GB", unit: 65 },
    { value: "70GB", label: "70GB", unit: 70 },
    { value: "75GB", label: "75GB", unit: 75 },
    { value: "80GB", label: "80GB", unit: 80 },
    { value: "85GB", label: "85GB", unit: 85 },
    { value: "90GB", label: "90GB", unit: 90 },
    { value: "95GB", label: "95GB", unit: 95 },
    { value: "100GB", label: "100GB", unit: 100 },
    { value: "150GB", label: "150GB", unit: 150 },
    { value: "200GB", label: "200GB", unit: 200 },
    { value: "250GB", label: "250GB", unit: 250 },
    { value: "300GB", label: "300GB", unit: 300 },
    { value: "350GB", label: "350GB", unit: 350 },
    { value: "400GB", label: "400GB", unit: 400 },
    { value: "450GB", label: "450GB", unit: 450 },
    { value: "500GB", label: "500GB", unit: 500 },
    { value: "600GB", label: "600GB", unit: 600 },
    { value: "700GB", label: "700GB", unit: 700 },
    { value: "800GB", label: "800GB", unit: 800 },
    { value: "900GB", label: "900GB", unit: 900 },
    { value: "1000GB", label: "1TB", unit: 1000 },
  ].filter((option) => option.unit >= baseFlavor.disk);

  // Initialize states for pre-selected values
  const [selectedVCPU, setSelectedVCPU] = useState(null);
  const [selectedRAM, setSelectedRAM] = useState(null);
  const [selectedStorage, setSelectedStorage] = useState(null);
  const hasInitializedModal = useRef(false);

  // Initialize resizeForm before useEffect
  const resizeForm = useFormik({
    initialValues: {
      newFlavor: "",
    },
    validationSchema: Yup.object({
      newFlavor:
        resizeType === "preconfigured"
          ? Yup.string().required("Please select a new flavor")
          : Yup.string(),
    }),
    onSubmit: async (values) => {
      let resizeServerData;
      let totalCost;

      if (resizeType === "preconfigured") {
        const selectedFlavor = preconfiguredOptions.find(
          (option) => option.value === values.newFlavor
        );
        const flavor = dewsListMap.find((f) => f.id === values.newFlavor);
        totalCost = currency === "NGN" ? flavor?.price || 0 : flavor?.dollarPrice || 0;

        resizeServerData = {
          serverId: selectedServer.serverId,
          flavorRef: values.newFlavor,
          flavor_id: selectedFlavor?.flavorId,
          projectId: selectedProject.id,
        };
      } else {
        const foundRam = RAMOptions.find(
          (ramOption) => ramOption.unit === lastCustomizedFlavor?.ram
        );
        const foundStorage = StorageOptions.find(
          (storageOption) => storageOption.unit === lastCustomizedFlavor?.disk
        );
        totalCost = calculateEstimatedCost(
          lastCustomizedFlavor?.vCPUs || selectedVCPU,
          foundRam?.value || selectedRAM,
          foundStorage?.value || selectedStorage
        ).numeric;

        resizeServerData = {
          serverId: selectedServer.serverId,
          projectId: selectedProject.id,
          custom_flavor_ram: foundRam?.unit || lastCustomizedFlavor?.ram,
          custom_flavor_disc: foundStorage?.unit || lastCustomizedFlavor?.disk,
          custom_flavor_vpcu: lastCustomizedFlavor?.vCPUs || selectedVCPU,
        };
      }

      if (totalCost > walletBalance) {
        setShowPaymentModal(true);
      } else {
        setIsLoadingInstance(true);
        try {
          const { payload } = await dispatch(
            resizeServer({ resizeServerData, navigate })
          );

          if (payload.statuscode === "00") {
            if (resizeType === "customized") {
              setCustomFlavorId(payload.flavor_id);
            }
            setTimeout(() => {
              setIsLoadingInstance(false);
              setShowConfirmModal(true);
              setContinueResize(resizeServerData);
            }, 20000);
          } else {
            toast.error(
              "Failed to initiate resize: " + (payload?.message || "Unknown error")
            );
            setIsLoadingInstance(false);
          }
        } catch (error) {
          toast.error("Error initiating resize: " + error.message);
          setIsLoadingInstance(false);
        }
      }
    },
  });

  useEffect(() => {
    if (customizeModal && !hasInitializedModal.current) {
      const newVCPU =
        lastCustomizedFlavor?.vCPUs ||
        currentFlavor.vCPUs ||
        VCPUOptions[0]?.value ||
        1;
      const newRAM = lastCustomizedFlavor?.ram
        ? RAMOptions.find((opt) => opt.unit === lastCustomizedFlavor.ram)?.value
        : RAMOptions.find((opt) => opt.unit === currentFlavor.ram)?.value ||
          RAMOptions[0]?.value ||
          "1GB";
      const newStorage = lastCustomizedFlavor?.disk
        ? StorageOptions.find((opt) => opt.unit === lastCustomizedFlavor.disk)
            ?.value
        : StorageOptions.find((opt) => opt.unit === currentFlavor.disk)?.value ||
          StorageOptions[0]?.value ||
          "5GB";

      setSelectedVCPU(newVCPU);
      setSelectedRAM(newRAM);
      setSelectedStorage(newStorage);

      hasInitializedModal.current = true;
    } else if (!customizeModal) {
      hasInitializedModal.current = false;
    }
  }, [customizeModal, VCPUOptions, RAMOptions, StorageOptions, lastCustomizedFlavor, currentFlavor]);

  const calculateEstimatedCost = (vcpu, ram, storage) => {
    const foundVCPU = vcpu || currentFlavor.vCPUs;
    const foundRam =
      RAMOptions.find((ramOption) => ramOption.value === ram) || {
        unit: currentFlavor.ram,
      };
    const foundStorage =
      StorageOptions.find((storageOption) => storageOption.value === storage) || {
        unit: currentFlavor.disk,
      };

    const VCPUPrice = currency === "NGN" ? 2600 : 1.8;
    const RAMPrice = currency === "NGN" ? 2500 : 1.7;
    const StoragePrice = currency === "NGN" ? 300 : 0.2;
    const IPAddressPrice = currency === "NGN" ? 3500 : 2.5;

    const vcpuValue = parseFloat(foundVCPU);
    const ramValue = parseFloat(foundRam.unit);
    const storageValue = parseFloat(foundStorage.unit);

    const totalCost =
      vcpuValue * VCPUPrice +
      ramValue * RAMPrice +
      storageValue * StoragePrice +
      IPAddressPrice;

    return {
      formatted: totalCost.toLocaleString("en-US", {
        style: "currency",
        currency: currency === "NGN" ? "NGN" : "USD",
      }),
      numeric: totalCost,
    };
  };

  // Calculate price for currentFlavor if not found in dewsListMap or if it's a custom flavor
  const calculatedCurrentPrice = calculateEstimatedCost(
    currentFlavor.vCPUs,
    RAMOptions.find((opt) => opt.unit === currentFlavor.ram)?.value ||
      `${currentFlavor.ram}GB`,
    StorageOptions.find((opt) => opt.unit === currentFlavor.disk)?.value ||
      `${currentFlavor.disk}GB`
  );

  // Update currentFlavor with calculated price if price is 0 or if it's a custom flavor
  if (currentFlavor.price === 0 || isCustomServer) {
    currentFlavor.price = calculatedCurrentPrice.numeric;
    currentFlavor.dollarPrice =
      currency === "USD" ? calculatedCurrentPrice.numeric : 0;
  }

  useEffect(() => {
    if (resizeType === "customized" && selectedVCPU && selectedRAM && selectedStorage) {
      const estimate = calculateEstimatedCost(selectedVCPU, selectedRAM, selectedStorage);
      setCalculatedEstimate(estimate.formatted);
    } else if (resizeType === "customized" && lastCustomizedFlavor) {
      const estimate = calculateEstimatedCost(
        lastCustomizedFlavor.vCPUs,
        RAMOptions.find((opt) => opt.unit === lastCustomizedFlavor.ram)?.value,
        StorageOptions.find((opt) => opt.unit === lastCustomizedFlavor.disk)?.value
      );
      setCalculatedEstimate(estimate.formatted);
    } else if (resizeType === "preconfigured" && resizeForm.values.newFlavor) {
      const flavor = dewsListMap.find((f) => f.id === resizeForm.values.newFlavor);
      if (flavor) {
        const estimate = currency === "NGN" ? flavor.price : flavor.dollarPrice;
        setCalculatedEstimate(
          estimate.toLocaleString("en-US", {
            style: "currency",
            currency: currency === "NGN" ? "NGN" : "USD",
          })
        );
      }
    } else {
      setCalculatedEstimate(
        (currency === "NGN" ? currentFlavor.price : currentFlavor.dollarPrice).toLocaleString(
          "en-US",
          {
            style: "currency",
            currency: currency === "NGN" ? "NGN" : "USD",
          }
        )
      );
    }
  }, [
    selectedVCPU,
    selectedRAM,
    selectedStorage,
    currency,
    resizeType,
    lastCustomizedFlavor,
    resizeForm.values.newFlavor,
    currentFlavor,
    dewsListMap,
  ]);

  const selectedFlavor =
    resizeType === "preconfigured"
      ? resizeForm.values.newFlavor
        ? dewsListMap.find((flavor) => flavor.id === resizeForm.values.newFlavor)
        : currentFlavor
      : lastCustomizedFlavor || currentFlavor;

  const handleConfirmResize = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    let confirmResizeServerData;

    if (resizeType === "preconfigured") {
      confirmResizeServerData = continueResize;
    } else {
      const foundRam = RAMOptions.find(
        (ramOption) => ramOption.unit === lastCustomizedFlavor?.ram
      );
      const foundStorage = StorageOptions.find(
        (storageOption) => storageOption.unit === lastCustomizedFlavor?.disk
      );

      confirmResizeServerData = {
        serverId: selectedServer.serverId,
        projectId: selectedProject.id,
        flavor_id: customFlavorId,
        custom_flavor_ram: foundRam?.unit || lastCustomizedFlavor?.ram,
        custom_flavor_disc: foundStorage?.unit || lastCustomizedFlavor?.disk,
        custom_flavor_vpcu: lastCustomizedFlavor?.vCPUs || selectedVCPU,
      };
    }

    try {
      const { payload } = await dispatch(
        confirmResizeServer({
          confirmResizeServerData,
          navigate,
        })
      );

      if (payload?.statuscode === "00") {
        setShowConfirmModal(false);
        setContinueResize(null);
        setCustomFlavorId(null);
        setTimeout(() => navigate("/client-area"), 2000);
      } else {
        toast.error(
          "Failed to confirm resize: " + (payload?.message || "Unknown error")
        );
      }
    } catch (error) {
      toast.error("An error occurred while confirming resize: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStopResize = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    let stopResizeServerData;

    if (resizeType === "preconfigured") {
      stopResizeServerData = continueResize;
    } else {
      stopResizeServerData = {
        serverId: selectedServer.serverId,
        projectId: selectedProject.id,
        flavor_id: customFlavorId,
      };
    }

    try {
      const { payload } = await dispatch(
        stopResizeServer({
          stopResizeServerData,
          navigate,
        })
      );

      if (payload?.statuscode === "00") {
        setShowConfirmModal(false);
        setContinueResize(null);
        setCustomFlavorId(null);
        setTimeout(() => navigate("/client-area"), 2000);
      } else {
        toast.error(
          "Failed to stop resize: " + (payload?.message || "Unknown error")
        );
      }
    } catch (error) {
      toast.error("An error occurred while stopping resize: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveConfiguration = () => {
    if (!selectedVCPU || !selectedRAM || !selectedStorage) {
      toast.error("Please ensure all configuration options are selected.");
      return;
    }

    const newCustomFlavor = {
      name: "Customized Flavor",
      vCPUs: selectedVCPU,
      ram: RAMOptions.find((ramOption) => ramOption.value === selectedRAM)?.unit || parseInt(selectedRAM.replace("GB", "")),
      disk: StorageOptions.find((storageOption) => storageOption.value === selectedStorage)?.unit || parseInt(selectedStorage.replace("GB", "")),
      price: calculateEstimatedCost(selectedVCPU, selectedRAM, selectedStorage).numeric,
    };

    setLastCustomizedFlavor(newCustomFlavor);
    setCustomizeModal(false);
    setResizeType("customized");
  };

  const handleCloseCustomizeModal = () => {
    setCustomizeModal(false);
    if (!lastCustomizedFlavor && resizeType === "customized") {
      setResizeType("preconfigured");
    }
  };

  // Calculate total cost for button text
  const totalCost =
    resizeType === "preconfigured" && resizeForm.values.newFlavor
      ? currency === "NGN"
        ? dewsListMap.find((f) => f.id === resizeForm.values.newFlavor)?.price || 0
        : dewsListMap.find((f) => f.id === resizeForm.values.newFlavor)?.dollarPrice || 0
      : resizeType === "customized"
      ? calculateEstimatedCost(
          lastCustomizedFlavor?.vCPUs || selectedVCPU,
          RAMOptions.find((opt) => opt.unit === lastCustomizedFlavor?.ram)?.value || selectedRAM,
          StorageOptions.find((opt) => opt.unit === lastCustomizedFlavor?.disk)?.value || selectedStorage
        ).numeric
      : currency === "NGN" ? currentFlavor.price : currentFlavor.dollarPrice;

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      {isFetchingServer ? (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isFetchingServer}
        >
          <CircularProgress color="inherit" />
          <p className="ml-3">Fetching server details...</p>
        </Backdrop>
      ) : (
        <>
          <p className="crimson-font text-lg font-medium text-primaryBlue">
            Resize instance for {selectedServer.serverName} server:
          </p>
          <p className="dark:text-gray-500 text-gray-400 text-sm mb-6 leading-loose md:leading-normal">
            Resizing an instance allows you to upgrade to a larger flavor with more
            resources. You can only resize to a larger flavor, not a smaller one.
          </p>

          <div className="grid gap-4">
            <form onSubmit={resizeForm.handleSubmit}>
              <div className="w-full grid grid-cols-1 lg:grid-cols-2 items-start gap-4">
                <div className="w-full grid grid-cols-1 items-start gap-4">
                  <div>
                    <label className="dark:text-white text-sm mb-1 block">
                      Current Server ID
                    </label>
                    <Input type="text" value={currentFlavor?.flavorId} disabled />
                  </div>

                  <div>
                    <label className="dark:text-white text-sm mb-1 block">
                      Resize Type
                    </label>
                    <Select
                      name="resizeType"
                      options={[
                        { value: "preconfigured", label: "Preconfigured Flavor" },
                        { value: "customized", label: "Customized Flavor" },
                      ]}
                      value={resizeType}
                      onChange={(e) => {
                        setResizeType(e.target.value);
                        if (e.target.value === "customized") {
                          setCustomizeModal(true);
                        } else {
                          setCustomizeModal(false);
                          resizeForm.setFieldValue("newFlavor", "");
                        }
                      }}
                    />
                  </div>

                  {resizeType === "preconfigured" && (
                    <Select
                      name="newFlavor"
                      label="New Flavor *"
                      options={preconfiguredOptions}
                      value={resizeForm.values.newFlavor}
                      onBlur={resizeForm.handleBlur}
                      onChange={(event) => {
                        resizeForm.setFieldValue("newFlavor", event.target.value);
                        const flavor = dewsListMap.find((f) => f.id === event.target.value);
                        if (flavor) {
                          setLastPreconfiguredFlavor({
                            name: flavor.name,
                            vCPUs: flavor.vCPUs,
                            ram: flavor.ram,
                            disk: flavor.disk,
                            price: flavor.price,
                          });
                        }
                      }}
                      onError={
                        resizeForm.touched.newFlavor && resizeForm.errors.newFlavor
                          ? resizeForm.errors.newFlavor
                          : null
                      }
                    />
                  )}
                </div>

                <div className="w-full h-full flex flex-col justify-start items-start bg-white dark:bg-slate-800 dark:text-white rounded-md p-6 gap-4 border-2 border-primaryBlue">
                  <h3 className="dark:text-white text-lg font-semibold mb-2 w-full border-b border-b-gray-400">
                    Selected Flavor Price
                  </h3>
                  <div className="grid grid-cols-1">
                    <span className="dark:text-white text-lg">
                      {selectedFlavor?.name}
                    </span>
                    <span className="!text-xl !lg:text-3xl !font-bold !text-primaryBlue">
                      <span
                        dangerouslySetInnerHTML={{
                          __html: currency === "NGN" ? "₦" : "$",
                        }}
                      />
                      {resizeType === "customized" && selectedFlavor?.price
                        ? selectedFlavor.price.toLocaleString(currency === "NGN" ? "en-NG" : "en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : selectedFlavor
                        ? currency === "NGN"
                          ? selectedFlavor?.price?.toLocaleString("en-NG", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          : selectedFlavor?.dollarPrice?.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }) ||
                            selectedFlavor?.price?.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                        : "0.00"}
                    </span>
                  </div>
                </div>
              </div>

              {selectedFlavor && (
                <div className="mt-6">
                  <h3 className="dark:text-white text-sm font-semibold mb-2">
                    Flavor Details
                  </h3>
                  <div className="border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-slate-800 dark:text-white">
                    <div className="grid grid-cols-2 p-3 border-b border-gray-300 dark:border-gray-600">
                      <span className="dark:text-white text-sm">Name</span>
                      <span className="dark:text-white text-sm">
                        {selectedFlavor?.name}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 p-3 border-b border-gray-300 dark:border-gray-600">
                      <span className="dark:text-white text-sm">VCPUs</span>
                      <span className="dark:text-white text-sm">
                        {selectedFlavor?.vCPUs}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 p-3 border-b border-gray-300 dark:border-gray-600">
                      <span className="dark:text-white text-sm">Root Disk</span>
                      <span className="dark:text-white text-sm">
                        {selectedFlavor?.disk} GB
                      </span>
                    </div>
                    <div className="grid grid-cols-2 p-3">
                      <span className="dark:text-white text-sm">RAM</span>
                      <span className="dark:text-white text-sm">
                        {selectedFlavor?.ram} GB
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 w-full flex justify-end items-end gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/client-area")}
                  className="p-3 rounded-md bg-gray-200 dark:bg-gray-600 text-sm dark:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="p-3 rounded-md bg-primaryBlue text-white text-sm"
                  disabled={isFetchingServer || isLoadingInstance}
                >
                  {totalCost > walletBalance ? "Pay Now" : "Initiate Resize"}
                </button>
              </div>
            </form>
          </div>

          {customizeModal && (
            <div className="fixed w-full bg-black/60 top-0 left-0 z-50 h-screen">
              <div className="flex h-full items-center justify-center p-4">
                <div className="bg-white dark:bg-[#141D26] dark:text-white w-full md:w-1/3 rounded max-h-[100%] md:max-h-[90%]">
                  <div className="px-6 md:px-10 py-6 border-b dark:border-b-[#c4c4c432] flex justify-between items-center">
                    <span className="text-primaryBlue font-medium">
                      Customize your flavor
                    </span>
                    <span
                      onClick={handleCloseCustomizeModal}
                      className="cursor-pointer"
                    >
                      <img src={closeIcon} alt="Close" className="h-5" />
                    </span>
                  </div>
                  <div className="py-10 grid gap-4">
                    <div className="px-6 md:px-10 grid gap-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Select
                          label={"VCPUs"}
                          name="vcpus"
                          options={VCPUOptions}
                          value={selectedVCPU || ""}
                          onChange={(e) => setSelectedVCPU(e.target.value)}
                          disabled={VCPUOptions.length === 0}
                        />
                        <Select
                          label={"RAM"}
                          name="ram"
                          options={RAMOptions}
                          value={selectedRAM || ""}
                          onChange={(e) => setSelectedRAM(e.target.value)}
                          disabled={RAMOptions.length === 0}
                        />
                        <Select
                          label={"Storage"}
                          name="storage"
                          options={StorageOptions}
                          value={selectedStorage || ""}
                          onChange={(e) => setSelectedStorage(e.target.value)}
                          disabled={StorageOptions.length === 0}
                        />
                      </div>
                      {(!VCPUOptions.length || !RAMOptions.length || !StorageOptions.length) && (
                        <p className="text-red-500 text-sm">
                          No valid options available for customization. Please select a different base flavor.
                        </p>
                      )}
                    </div>
                    <div className="px-6 md:px-10 grid gap-2">
                      <div>
                        <div className="text-xs">Estimated cost:</div>
                        <div className="font-semibold crimson-font text-primaryBlue text-lg">
                          {calculatedEstimate}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-sm md:text-xs rounded text-white bg-[#1DA1F2] px-8 py-4 cursor-pointer w-fit"
                        onClick={handleSaveConfiguration}
                        disabled={isLoadingInstance || !VCPUOptions.length || !RAMOptions.length || !StorageOptions.length}
                      >
                        Save Configuration
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showConfirmModal && (
            <div className="fixed w-full bg-black/60 top-0 left-0 z-50 h-screen">
              <div className="flex h-full items-center justify-center p-4">
                <div className="bg-white dark:bg-[#141D26] dark:text-white w-full md:w-1/3 rounded max-h-[100%] md:max-h-[60%]">
                  <div className="px-6 md:px-10 py-6 border-b dark:border-b-[#c4c4c432] flex justify-between items-center">
                    <span className="text-primaryBlue font-medium">
                      Confirm or Stop Resize Instance
                    </span>
                  </div>
                  <div className="w-full flex flex-col justify-center items-center p-3">
                    <h3 className="text-[16px] leading-[24px] text-black">
                      Are you sure you want to resize this instance?
                    </h3>
                  </div>
                  <div className="pgrid gap-4">
                    <div className="py-4 w-full flex justify-end items-end gap-3 px-3">
                      <Button
                        buttonRole={"submit"}
                        loading={isLoading}
                        handleClick={handleStopResize}
                        buttonText={"Stop Resize"}
                        bgColor="bg-gray-200 dark:bg-gray-600 text-sm dark:text-white"
                      />
                      <Button
                        buttonRole={"submit"}
                        loading={isLoading}
                        handleClick={handleConfirmResize}
                        buttonText={"Confirm Resize"}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <PaymentModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            currency={currency}
            totalPrice={totalCost}
            walletBalance={walletBalance}
            virtualAccount={virtualAccount}
            onPaymentSuccess={() => resizeForm.handleSubmit()}
          />

          {isLoadingInstance && (
            <Backdrop
              sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={isLoadingInstance}
            >
              <CircularProgress color="inherit" />
              <p className="ml-3">Initiating Resize...</p>
            </Backdrop>
          )}
        </>
      )}
    </div>
  );
};

export default ResizeInstance;

